import cx from 'classnames';
import PropTypes from 'prop-types';

import { InlineUpgradeButton } from 'src/components/InlineUpgradeButton';
import { UsageProgress } from 'src/components/UsageProgress';
import { FEATURE_LIMITS } from 'src/data/featureLimits';
import { isPlanFromBusinessCategory, planCanBeUpgraded } from 'src/data/subscription';
import { useGetUpgradeRowsLimitButtonAttributes } from 'src/services';
import { useCurrentCustomerPlan } from 'src/store';

import { useHandlers } from './useHandlers';
import { useStyles } from './useStyles';

export const FeatureLimitedProgressBar = ({
  className,
  feature,
  limitData = {},
  upgradeActionOrigin,
  headerLabel,
}) => {
  const styles = useStyles();

  const currentPlan = useCurrentCustomerPlan();
  const { currentCount, limit } = limitData;
  const isFeatureLimited = currentCount >= limit;
  const { upgradeButtonText, isBusinessPlan } = useGetUpgradeRowsLimitButtonAttributes();
  const upgradeVisible = shouldDisplayUpgrade({ currentPlan, limit, currentCount });

  const { handleUpgradeClick } = useHandlers({ upgradeActionOrigin, feature });

  return (
    <div className={cx(styles.root, className, { [styles.businessPlanWidth]: isBusinessPlan })}>
      <div className={styles.topContainer}>
        {headerLabel}

        {upgradeVisible && (
          <InlineUpgradeButton
            className={styles.upgradeButton}
            text={upgradeButtonText}
            onClick={handleUpgradeClick}
          />
        )}
      </div>
      <UsageProgress
        classes={{
          bar: isFeatureLimited ? styles.limitBar : styles.bar,
          linearProgress: styles.linearProgress,
        }}
        value={currentCount}
        limit={limit}
      />
    </div>
  );
};

FeatureLimitedProgressBar.displayName = 'FeatureLimitedProgressBar';
FeatureLimitedProgressBar.propTypes = {
  className: PropTypes.string,
  feature: PropTypes.oneOf(Object.values(FEATURE_LIMITS)).isRequired,
  limitData: PropTypes.shape({
    currentCount: PropTypes.number,
    limit: PropTypes.number,
  }).isRequired,
  upgradeActionOrigin: PropTypes.string,
  headerLabel: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
};

FeatureLimitedProgressBar.defaultProps = {
  className: null,
  upgradeActionOrigin: '',
  headerLabel: '',
};

const shouldDisplayUpgrade = ({ currentPlan, limit, currentCount }) => {
  if (isPlanFromBusinessCategory(currentPlan)) {
    return currentCount >= limit;
  }

  return planCanBeUpgraded(currentPlan);
};
